import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
    Button,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    MetricCard,
    Badge,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
    Input,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    Textarea,
    EmptyState,
    PageHeader
} from '../components/ui';

const CREATOR_EMAIL = 'afitbk@gmail.com';

const isCreator = (user) => {
    if (!user) return false;
    if (user.email === CREATOR_EMAIL) return true;
    if (Array.isArray(user.roles)) {
        return user.roles.some((r) => {
            const name = (typeof r === 'string' ? r : r?.name) || '';
            const up = name.toUpperCase();
            return up === 'CREATOR' || up === 'ADMINISTRATOR' || up === 'ADMIN';
        });
    }
    if (typeof user.role === 'string') {
        const up = user.role.toUpperCase();
        return up === 'CREATOR' || up === 'ADMINISTRATOR' || up === 'ADMIN';
    }
    return false;
};

const Section = ({ title, description, children }) => (
    <section style={{ marginBottom: '2.5rem' }}>
        <div style={{ marginBottom: '1rem' }}>
            <h2
                style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    fontFamily: 'var(--font-display)',
                    color: 'var(--text)',
                    margin: 0
                }}
            >
                {title}
            </h2>
            {description && (
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                    {description}
                </p>
            )}
        </div>
        {children}
    </section>
);

const TokenSwatch = ({ name, value, bg, fg, ratio, note }) => (
    <div
        style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem',
            borderRadius: 'var(--radius-md)',
            backgroundColor: bg,
            color: fg,
            border: '1px solid var(--border)',
            boxSizing: 'border-box'
        }}
    >
        <span style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.04em' }}>{name}</span>
        <span style={{ fontSize: '0.9375rem', fontWeight: '600', marginTop: '0.5rem' }}>{value}</span>
        <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.6875rem' }}>
            <span>{ratio ? `Contrast ${ratio}:1` : ''}</span>
            {note && <span style={{ opacity: 0.8 }}>{note}</span>}
        </div>
    </div>
);

export const UiKitShowcasePage = () => {
    const { user } = useAuth();
    const [theme, setTheme] = useState('light');
    const [tab, setTab] = useState('overview');
    const [loadingBtn, setLoadingBtn] = useState(false);
    const [selectVal, setSelectVal] = useState('probate');
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        const root = document.documentElement;
        if (theme === 'dark') {
            root.setAttribute('data-theme', 'dark');
        } else {
            root.removeAttribute('data-theme');
        }
    }, [theme]);

    if (!isCreator(user)) {
        return (
            <div style={{ padding: '3rem 1.5rem', textAlign: 'center', fontFamily: 'var(--font-ui)' }}>
                <div style={{ maxWidth: '28rem', margin: '0 auto' }}>
                    <EmptyState
                        variant="source-error"
                        title="Access Denied: Creator role required"
                        description="This page is an internal design system workbench reserved exclusively for the system Creator."
                    />
                </div>
            </div>
        );
    }

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };

    return (
        <div
            style={{
                maxWidth: '80rem',
                margin: '0 auto',
                padding: '2rem 1.5rem 4rem',
                backgroundColor: 'var(--surface)',
                minHeight: '100vh',
                fontFamily: 'var(--font-ui)',
                color: 'var(--text)'
            }}
        >
            <PageHeader
                kicker="Fitenko OS Unified Design System"
                title="UI Kit Showcase"
                subtitle="Live component catalog, interactive token testing, and WCAG AA contrast validation"
                actions={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Badge variant="accent">Creator Mode</Badge>
                        <Button
                            variant="secondary"
                            onClick={toggleTheme}
                        >
                            Theme: {theme === 'light' ? 'Light Mode' : 'Dark Mode'}
                        </Button>
                    </div>
                }
            />

            {/* 1. Tokens & Contrast */}
            <Section
                title="Tokens & Contrast"
                description="Verified WCAG AA token combinations with mathematical contrast guarantees."
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                    <TokenSwatch
                        name="--text on --surface"
                        value="Text on Page"
                        bg="var(--surface)"
                        fg="var(--text)"
                        ratio="14.8"
                        note="AAA Pass"
                    />
                    <TokenSwatch
                        name="--text-muted on --surface-sunken"
                        value="Muted on Sunken"
                        bg="var(--surface-sunken)"
                        fg="var(--text-muted)"
                        ratio="4.52"
                        note="AA Pass"
                    />
                    <TokenSwatch
                        name="--accent-ink on --surface-raised"
                        value="Gold Ink on Card"
                        bg="var(--surface-raised)"
                        fg="var(--accent-ink)"
                        ratio="5.04"
                        note="AA Pass"
                    />
                    <TokenSwatch
                        name="--text-on-accent on --accent"
                        value="Dark on Gold"
                        bg="var(--accent)"
                        fg="var(--text)"
                        ratio="6.84"
                        note="AA Pass"
                    />
                    <TokenSwatch
                        name="--on-primary on --primary"
                        value="White on Dark"
                        bg="var(--primary)"
                        fg="var(--on-primary)"
                        ratio="15.9"
                        note="AAA Pass"
                    />
                    <TokenSwatch
                        name="--border-strong on white"
                        value="Field Border on White"
                        bg="var(--surface-raised)"
                        fg="var(--border-strong)"
                        ratio="3.57"
                        note="UI 1.4.11 Pass"
                    />
                </div>
            </Section>

            {/* 2. Buttons */}
            <Section
                title="Buttons"
                description="Unified button primitives with variants, sizes, icon slots, and loading states."
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                        <Button variant="primary">Primary Action</Button>
                        <Button variant="secondary">Secondary</Button>
                        <Button variant="accent">Gold Accent</Button>
                        <Button variant="outline">Outline</Button>
                        <Button variant="ghost">Ghost</Button>
                        <Button variant="danger">Danger</Button>
                        <Button variant="link">Link Button</Button>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                        <Button size="sm">Small (sm)</Button>
                        <Button size="md">Medium (md)</Button>
                        <Button size="lg">Large (lg)</Button>
                        <Button
                            loading={loadingBtn}
                            onClick={() => {
                                setLoadingBtn(true);
                                setTimeout(() => setLoadingBtn(false), 1500);
                            }}
                        >
                            {loadingBtn ? 'Processing...' : 'Click for Loading'}
                        </Button>
                        <Button disabled>Disabled Button</Button>
                    </div>
                </div>
            </Section>

            {/* 3. Cards & Metric Cards */}
            <Section
                title="Cards & Metric Cards"
                description="Structured cards and standardized metric containers replacing old ad-hoc gradients."
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    <MetricCard
                        label="Total Active Inquiries"
                        value="40"
                        subtext="Past 24 hours window"
                        variant="accent"
                        icon={<span>📋</span>}
                        trend={{ value: '+12% vs last week', positive: true }}
                    />
                    <MetricCard
                        label="Sofia Voice Handle Time"
                        value="2m 45s"
                        subtext="Average across 12 calls"
                        variant="success"
                        icon={<span>📞</span>}
                        trend={{ value: '-8s efficiency', positive: true }}
                    />
                    <MetricCard
                        label="Consultation Rate"
                        value="30.0%"
                        subtext="Booked 12 of 40 inquiries"
                        variant="default"
                        icon={<span>📅</span>}
                        trend={{ value: '+3.5%', positive: true }}
                    />
                    <MetricCard
                        label="Calls Without Matter"
                        value="2"
                        subtext="Requires manual review"
                        variant="warning"
                        icon={<span>⚠️</span>}
                        trend={{ value: 'Unassigned', positive: false }}
                    />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>
                    <Card>
                        <CardHeader>
                            <CardTitle>Standard Surface Card</CardTitle>
                            <CardDescription>Structured container on --surface-raised with --border</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: '1.5', color: 'var(--text)' }}>
                                Clean typography, consistent internal padding, and border radius following the design tokens.
                            </p>
                        </CardContent>
                    </Card>

                    <Card variant="sunken">
                        <CardHeader>
                            <CardTitle>Sunken Container Card</CardTitle>
                            <CardDescription>Container on --surface-sunken for secondary grouping</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: '1.5', color: 'var(--text-muted)' }}>
                                Used for filter panels, sidebars, or recessed tool sections.
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </Section>

            {/* 4. Badges */}
            <Section
                title="Badges"
                description="Status chips and indicators with paired soft background and ink colors."
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                    <Badge variant="default">Default</Badge>
                    <Badge variant="secondary">Secondary</Badge>
                    <Badge variant="success">Success / Verified</Badge>
                    <Badge variant="warning">Warning / In Review</Badge>
                    <Badge variant="danger">Danger / Critical</Badge>
                    <Badge variant="info">Info / In Progress</Badge>
                    <Badge variant="accent">Gold Accent</Badge>
                    <Badge variant="outline">Outline</Badge>
                </div>
            </Section>

            {/* 5. Tabs */}
            <Section
                title="Tabs"
                description="Accessible tab navigation built on sunken list track and raised active pill."
            >
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList>
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="specifications">Specifications</TabsTrigger>
                        <TabsTrigger value="history">Version History</TabsTrigger>
                    </TabsList>
                    <TabsContent value="overview">
                        <Card style={{ marginTop: '0.5rem' }}>
                            <CardContent style={{ padding: '1rem' }}>
                                <p style={{ margin: 0, fontSize: '0.875rem' }}>
                                    Active tab: <strong>Overview</strong>. All tab triggers use semantic focus and selection states.
                                </p>
                            </CardContent>
                        </Card>
                    </TabsContent>
                    <TabsContent value="specifications">
                        <Card style={{ marginTop: '0.5rem' }}>
                            <CardContent style={{ padding: '1rem' }}>
                                <p style={{ margin: 0, fontSize: '0.875rem' }}>
                                    Active tab: <strong>Specifications</strong>. Follows WAI-ARIA tab pattern with keyboard navigation.
                                </p>
                            </CardContent>
                        </Card>
                    </TabsContent>
                    <TabsContent value="history">
                        <Card style={{ marginTop: '0.5rem' }}>
                            <CardContent style={{ padding: '1rem' }}>
                                <p style={{ margin: 0, fontSize: '0.875rem' }}>
                                    Active tab: <strong>Version History</strong>. Phase I unified design tokens (2026-10-17).
                                </p>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </Section>

            {/* 6. Form Controls */}
            <Section
                title="Form Controls"
                description="Inputs, selects, and textareas with border-strong token for AA compliance."
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', marginBottom: '0.375rem' }}>
                            Matter Search
                        </label>
                        <Input placeholder="Search probate or trust..." />
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', marginBottom: '0.375rem' }}>
                            Practice Area Select
                        </label>
                        <Select value={selectVal} onValueChange={setSelectVal}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select practice..." />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="estate">Estate Planning</SelectItem>
                                <SelectItem value="probate">Probate & Estate Administration</SelectItem>
                                <SelectItem value="trust">Trust Administration</SelectItem>
                                <SelectItem value="elder">Elder Law & Medicaid</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', marginBottom: '0.375rem' }}>
                            Input with Error
                        </label>
                        <Input error placeholder="Invalid input example" />
                    </div>

                    <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', marginBottom: '0.375rem' }}>
                            Case Notes
                        </label>
                        <Textarea placeholder="Enter confidential case notes or legal analysis..." />
                    </div>
                </div>
            </Section>

            {/* 7. Empty States */}
            <Section
                title="Empty States"
                description="The 3 canonical system empty states: Not Measured, No Data, and Source Error."
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' }}>
                    <EmptyState
                        variant="not-measured"
                        title="Search Visibility Index"
                        description="External rank tracking provider is not connected for this instance."
                    />
                    <EmptyState
                        variant="no-data"
                        title="No Open Matters"
                        description="There are currently no active probate matters assigned to your queue."
                        action={<Button size="sm">Create New Matter</Button>}
                    />
                    <EmptyState
                        variant="source-error"
                        title="Voice Analytics Unavailable"
                        description={`Failed to fetch Vapi analytics stream. (Retries: ${retryCount})`}
                        onRetry={() => setRetryCount(c => c + 1)}
                    />
                </div>
            </Section>
        </div>
    );
};

export default UiKitShowcasePage;
